import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { Organization } from '../../contexts/AuthContext';
import config from '../../config/config';

const OrganizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${config.apiBaseUrl}/organizations`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const result = await response.json();
      if (result.status === 'success') {
        setOrganizations(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch organizations');
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newOrg, setNewOrg] = useState<Partial<Organization>>({
    name: '',
    apps: {
      invoiceExtraction: false,
      documentChat: false
    },
    quota: {
      pages: 500,
      used: 0
    }
  });

  const handleAddOrganization = async () => {
    if (!newOrg.name) return;
    
    try {
      const response = await fetch(`${config.apiBaseUrl}/organizations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newOrg.name,
          apps: {
            invoiceExtraction: newOrg.apps?.invoiceExtraction || false,
            documentChat: newOrg.apps?.documentChat || false
          },
          quota: {
            pages: newOrg.quota?.pages || 500,
            used: 0
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create organization');
      }

      const result = await response.json();
      if (result.status === 'success') {
        await fetchOrganizations(); // Refresh the list
        setIsAddModalOpen(false);
        setNewOrg({
          name: '',
          apps: {
            invoiceExtraction: false,
            documentChat: false
          },
          quota: {
            pages: 500,
            used: 0
          }
        });
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
    }
  };

  const handleNavigateToOrgDetails = (org: Organization) => {
    navigate(`/admin/organizations/${org.id}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Organizations</h1>
        <button
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 focus:ring-primary"
          onClick={() => setIsAddModalOpen(true)}
        >
          Add Organization
        </button>
      </div>

      {/* Loading and Error States */}
      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Organizations Table */}
      {!loading && !error && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Organization
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Apps
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Quota
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Usage
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {organizations.map((org) => (
                  <tr 
                    key={org.id} 
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => handleNavigateToOrgDetails(org)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{org.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">ID: {org.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {org.apps.invoiceExtraction && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100 mr-2">
                            Invoice Extraction
                          </span>
                        )}
                        {org.apps.documentChat && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
                            Document Chat
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{org.quota.pages} pages</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm text-gray-900 dark:text-gray-100 mr-2">
                          {Math.round((org.quota.used / org.quota.pages) * 100)}%
                        </div>
                        <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full" 
                            style={{ width: `${(org.quota.used / org.quota.pages) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {org.quota.used} of {org.quota.pages} pages used
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                      <button 
                        className="text-primary hover:text-blue-700 dark:hover:text-blue-400 mr-4"
                      >
                        Edit
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-800 dark:hover:text-red-400"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Organization Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Add New Organization</h3>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="orgName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Organization Name
                </label>
                <input
                  type="text"
                  id="orgName"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
                  value={newOrg.name}
                  onChange={(e) => setNewOrg({...newOrg, name: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Apps
                </label>
                <div className="flex flex-col space-y-2">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-600 dark:bg-gray-700"
                      checked={newOrg.apps?.invoiceExtraction}
                      onChange={(e) => setNewOrg({
                        ...newOrg,
                        apps: {
                          invoiceExtraction: e.target.checked,
                          documentChat: newOrg.apps?.documentChat || false
                        }
                      })}
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Invoice Extraction</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-600 dark:bg-gray-700"
                      checked={newOrg.apps?.documentChat}
                      onChange={(e) => setNewOrg({
                        ...newOrg,
                        apps: {
                          invoiceExtraction: newOrg.apps?.invoiceExtraction || false,
                          documentChat: e.target.checked
                        }
                      })}
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Document Chat</span>
                  </label>
                </div>
              </div>
              
              <div>
                <label htmlFor="quota" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Page Quota
                </label>
                <input
                  type="number"
                  id="quota"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
                  value={newOrg.quota?.pages}
                  onChange={(e) => setNewOrg({
                    ...newOrg,
                    quota: {
                      pages: parseInt(e.target.value) || 0,
                      used: newOrg.quota?.used || 0
                    }
                  })}
                />
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-offset-gray-800"
                onClick={() => setIsAddModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-offset-gray-800"
                onClick={handleAddOrganization}
              >
                Add Organization
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationsPage;